import { useState, useEffect } from "react";
import { subirDatos, bajarDatosSingle, subirDatosSingle } from "./syncSupabase";
import { STORAGE_CUENTAS } from "./FinanzasConfig";

const STORAGE_ASIENTOS = 'alqos_asientos';
const STORAGE_LINEAS = 'alqos_asientos_lineas';
const STORAGE_CONFIG = 'alqos_finanzas_config';

const LINEA_VACIA = { cuentaId: '', debe: '', haber: '', glosa: '', centroCostoId: '' };

function hoy() { return new Date().toISOString().slice(0, 10); }

function signoParaTipo(tipo) {
  // activo/egreso: +debe -haber · pasivo/patrimonio/ingreso: +haber -debe
  return (tipo === 'pasivo' || tipo === 'patrimonio' || tipo === 'ingreso') ? -1 : 1;
}

export default function AsientosContables({ onVolver, oscuro, usuario }) {
  const [asientos, setAsientos] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [fecha, setFecha] = useState(hoy());
  const [concepto, setConcepto] = useState('');
  const [filas, setFilas] = useState([{ ...LINEA_VACIA }, { ...LINEA_VACIA }]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    (async () => {
      await bajarDatosSingle(STORAGE_CONFIG, 'finanzas_config');
      try { setAsientos(JSON.parse(localStorage.getItem(STORAGE_ASIENTOS) || '[]')); } catch {}
      try { setLineas(JSON.parse(localStorage.getItem(STORAGE_LINEAS) || '[]')); } catch {}
      try { setCuentas(JSON.parse(localStorage.getItem(STORAGE_CUENTAS) || '[]')); } catch {}
      try { setCentrosCosto(JSON.parse(localStorage.getItem('alqos_centros_costo') || '[]')); } catch {}
      setCargando(false);
    })();
    const onSync = () => {
      try { setAsientos(JSON.parse(localStorage.getItem(STORAGE_ASIENTOS) || '[]')); } catch {}
      try { setLineas(JSON.parse(localStorage.getItem(STORAGE_LINEAS) || '[]')); } catch {}
      try { setCuentas(JSON.parse(localStorage.getItem(STORAGE_CUENTAS) || '[]')); } catch {}
    };
    window.addEventListener('supabase-sync-complete', onSync);
    return () => window.removeEventListener('supabase-sync-complete', onSync);
  }, []);

  const cuentasActivas = cuentas.filter(c => c.activo !== false);

  const guardarAsientos = (lista) => {
    setAsientos(lista);
    localStorage.setItem(STORAGE_ASIENTOS, JSON.stringify(lista));
    subirDatos(STORAGE_ASIENTOS);
  };
  const guardarLineas = (lista) => {
    setLineas(lista);
    localStorage.setItem(STORAGE_LINEAS, JSON.stringify(lista));
    subirDatos(STORAGE_LINEAS);
  };
  const guardarCuentas = (lista) => {
    setCuentas(lista);
    localStorage.setItem(STORAGE_CUENTAS, JSON.stringify(lista));
    subirDatos(STORAGE_CUENTAS);
  };

  const totalDebe = filas.reduce((s, f) => s + Number(f.debe || 0), 0);
  const totalHaber = filas.reduce((s, f) => s + Number(f.haber || 0), 0);
  const balanceado = totalDebe > 0 && totalDebe === totalHaber;

  const setFila = (i, k, v) => setFilas(fs => fs.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const agregarFila = () => setFilas(fs => [...fs, { ...LINEA_VACIA }]);
  const quitarFila = (i) => setFilas(fs => fs.length > 2 ? fs.filter((_, idx) => idx !== i) : fs);

  const abrirNuevo = () => {
    setFecha(hoy()); setConcepto(''); setFilas([{ ...LINEA_VACIA }, { ...LINEA_VACIA }]);
    setShowForm(true);
  };

  const guardarBorrador = async () => {
    if (!concepto.trim()) return alert('El concepto es obligatorio');
    const filasValidas = filas.filter(f => f.cuentaId && (Number(f.debe) > 0 || Number(f.haber) > 0));
    if (filasValidas.length < 2) return alert('Cargá al menos 2 líneas con cuenta y monto');
    if (!balanceado) return alert('La suma del Debe debe ser igual a la suma del Haber');

    const rawConfig = localStorage.getItem(STORAGE_CONFIG);
    const config = rawConfig ? JSON.parse(rawConfig) : { ultimoAsiento: 0 };
    const nroNuevo = (config.ultimoAsiento || 0) + 1;
    const numero = `A-${new Date().getFullYear()}-${String(nroNuevo).padStart(5, '0')}`;

    const asientoId = Math.floor(Date.now());
    const nuevoAsiento = {
      id: asientoId, numero, fecha, concepto,
      origen: 'manual', origenId: null,
      totalDebe, totalHaber, estado: 'borrador',
      createdBy: usuario?.nombre || 'Sistema', createdAt: new Date().toISOString(),
    };
    const nuevasLineas = filasValidas.map((f, i) => ({
      id: asientoId + i + 1, asientoId,
      cuentaId: Number(f.cuentaId), debe: Number(f.debe || 0), haber: Number(f.haber || 0),
      glosa: f.glosa, centroCostoId: f.centroCostoId ? Number(f.centroCostoId) : null,
    }));

    guardarAsientos([nuevoAsiento, ...asientos]);
    guardarLineas([...lineas, ...nuevasLineas]);

    const nuevaConfig = { ...config, ultimoAsiento: nroNuevo };
    localStorage.setItem(STORAGE_CONFIG, JSON.stringify(nuevaConfig));
    subirDatosSingle(STORAGE_CONFIG, 'finanzas_config');

    setShowForm(false);
  };

  const confirmarAsiento = (asiento) => {
    if (asiento.estado !== 'borrador') return;
    const lineasAsiento = lineas.filter(l => l.asientoId === asiento.id);
    const cuentasActualizadas = cuentas.map(c => {
      const impacto = lineasAsiento.filter(l => l.cuentaId === c.id);
      if (!impacto.length) return c;
      const delta = impacto.reduce((s, l) => s + signoParaTipo(c.tipo) * (l.debe - l.haber), 0);
      return { ...c, saldo: Number((c.saldo || 0) + delta) };
    });
    guardarCuentas(cuentasActualizadas);
    guardarAsientos(asientos.map(a => a.id === asiento.id ? { ...a, estado: 'confirmado' } : a));
    setDetalle(null);
  };

  const anularAsiento = (asiento) => {
    if (asiento.estado !== 'confirmado') return;
    if (!window.confirm(`¿Anular el asiento ${asiento.numero}? Se revierte el impacto en los saldos.`)) return;
    const lineasAsiento = lineas.filter(l => l.asientoId === asiento.id);
    const cuentasActualizadas = cuentas.map(c => {
      const impacto = lineasAsiento.filter(l => l.cuentaId === c.id);
      if (!impacto.length) return c;
      const delta = impacto.reduce((s, l) => s + signoParaTipo(c.tipo) * (l.debe - l.haber), 0);
      return { ...c, saldo: Number((c.saldo || 0) - delta) };
    });
    guardarCuentas(cuentasActualizadas);
    guardarAsientos(asientos.map(a => a.id === asiento.id ? { ...a, estado: 'anulado' } : a));
    setDetalle(null);
  };

  const inp = { width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' };
  const lbl = { fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px', display: 'block' };
  const ESTADOS = { borrador: { label: 'Borrador', color: '#d97706' }, confirmado: { label: 'Confirmado', color: '#16a34a' }, anulado: { label: 'Anulado', color: '#dc2626' } };

  if (cargando) return <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>Cargando asientos…</div>;

  return (
    <div style={{ padding: '24px', fontFamily: 'Inter, sans-serif', background: '#E8E9F6', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={onVolver} style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#6B7280' }}>← Menú</button>
          <span style={{ fontSize: '22px' }}>📑</span>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1C3FAA', margin: 0 }}>Asientos Contables</h1>
        </div>
        <button onClick={abrirNuevo} style={{ background: '#1C3FAA', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 18px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>+ Nuevo asiento</button>
      </div>

      <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Número', 'Fecha', 'Concepto', 'Debe', 'Haber', 'Estado', ''].map(h => (
                <th key={h} style={{ padding: '10px 14px', fontSize: '11px', fontWeight: '600', color: '#6B7280', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {asientos.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: '60px', textAlign: 'center', color: '#9CA3AF' }}>Sin asientos cargados</td></tr>
            ) : asientos.map(a => (
              <tr key={a.id} onClick={() => setDetalle(a)} style={{ cursor: 'pointer' }}>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', fontFamily: 'monospace', fontSize: '12px', fontWeight: '600' }}>{a.numero}</td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', fontSize: '12px' }}>{a.fecha}</td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}>{a.concepto}</td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', fontSize: '12px' }}>${Number(a.totalDebe).toLocaleString('es-AR')}</td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', fontSize: '12px' }}>${Number(a.totalHaber).toLocaleString('es-AR')}</td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ background: `${ESTADOS[a.estado]?.color}18`, color: ESTADOS[a.estado]?.color, fontSize: '11px', padding: '2px 8px', borderRadius: '6px', fontWeight: '600' }}>{ESTADOS[a.estado]?.label}</span>
                </td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6' }} onClick={e => e.stopPropagation()}>
                  {a.estado === 'borrador' && <button onClick={() => confirmarAsiento(a)} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Confirmar</button>}
                  {a.estado === 'confirmado' && <button onClick={() => anularAsiento(a)} style={{ background: 'none', border: '1px solid #fee2e2', color: '#dc2626', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Anular</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '720px', maxHeight: '92vh', overflowY: 'auto', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1C3FAA', margin: 0 }}>+ Nuevo asiento</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={lbl}>Fecha</label>
                <input style={inp} type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Concepto *</label>
                <input style={inp} placeholder="Ej: Cobro factura VTA-2026-0012" value={concepto} onChange={e => setConcepto(e.target.value)} />
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <thead>
                <tr>
                  {['Cuenta', 'Debe', 'Haber', 'Glosa', 'C. Costo', ''].map(h => (
                    <th key={h} style={{ padding: '6px', fontSize: '10px', fontWeight: '600', color: '#6B7280', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map((f, i) => (
                  <tr key={i}>
                    <td style={{ padding: '4px' }}>
                      <select style={inp} value={f.cuentaId} onChange={e => setFila(i, 'cuentaId', e.target.value)}>
                        <option value="">— Cuenta —</option>
                        {cuentasActivas.map(c => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '4px', width: '90px' }}><input style={inp} type="number" value={f.debe} onChange={e => setFila(i, 'debe', e.target.value)} /></td>
                    <td style={{ padding: '4px', width: '90px' }}><input style={inp} type="number" value={f.haber} onChange={e => setFila(i, 'haber', e.target.value)} /></td>
                    <td style={{ padding: '4px' }}><input style={inp} value={f.glosa} onChange={e => setFila(i, 'glosa', e.target.value)} /></td>
                    <td style={{ padding: '4px', width: '130px' }}>
                      <select style={inp} value={f.centroCostoId} onChange={e => setFila(i, 'centroCostoId', e.target.value)}>
                        <option value="">—</option>
                        {centrosCosto.map(cc => <option key={cc.id} value={cc.id}>{cc.nombre}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '4px' }}>
                      <button onClick={() => quitarFila(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={agregarFila} style={{ background: 'none', border: '1px dashed #E5E7EB', color: '#6B7280', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', marginBottom: '16px' }}>+ Agregar línea</button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: balanceado ? '#f0fdf4' : '#fef2f2', borderRadius: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: balanceado ? '#16a34a' : '#dc2626' }}>
                Debe: ${totalDebe.toLocaleString('es-AR')} · Haber: ${totalHaber.toLocaleString('es-AR')} {balanceado ? '✓ Balanceado' : '⚠ No balanceado'}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #E5E7EB', background: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#6B7280' }}>Cancelar</button>
              <button onClick={guardarBorrador} disabled={!balanceado} style={{ flex: 2, padding: '10px', borderRadius: '10px', border: 'none', background: balanceado ? '#1C3FAA' : '#9CA3AF', color: 'white', fontSize: '13px', fontWeight: '600', cursor: balanceado ? 'pointer' : 'not-allowed' }}>Guardar borrador</button>
            </div>
          </div>
        </div>
      )}

      {detalle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setDetalle(null)}>
          <div style={{ background: 'white', borderRadius: '16px', width: '520px', padding: '24px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1C3FAA', margin: '0 0 12px' }}>{detalle.numero} — {detalle.concepto}</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead><tr><th style={{ textAlign: 'left', padding: '4px' }}>Cuenta</th><th style={{ textAlign: 'right', padding: '4px' }}>Debe</th><th style={{ textAlign: 'right', padding: '4px' }}>Haber</th></tr></thead>
              <tbody>
                {lineas.filter(l => l.asientoId === detalle.id).map(l => {
                  const c = cuentas.find(c => c.id === l.cuentaId);
                  return (
                    <tr key={l.id}>
                      <td style={{ padding: '4px' }}>{c ? `${c.codigo} — ${c.nombre}` : l.cuentaId}</td>
                      <td style={{ padding: '4px', textAlign: 'right' }}>{l.debe ? `$${Number(l.debe).toLocaleString('es-AR')}` : '—'}</td>
                      <td style={{ padding: '4px', textAlign: 'right' }}>{l.haber ? `$${Number(l.haber).toLocaleString('es-AR')}` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
              {detalle.estado === 'borrador' && <button onClick={() => confirmarAsiento(detalle)} style={{ flex: 1, background: '#16a34a', color: 'white', border: 'none', borderRadius: '10px', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Confirmar</button>}
              {detalle.estado === 'confirmado' && <button onClick={() => anularAsiento(detalle)} style={{ flex: 1, background: 'none', border: '1px solid #fee2e2', color: '#dc2626', borderRadius: '10px', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Anular</button>}
              <button onClick={() => setDetalle(null)} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #E5E7EB', background: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#6B7280' }}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
